import { useGetAllArtworksCoreInfo } from "@/api/artwork/artwork";
import { PagingParameters } from "@/api/dtos";
import { useGetAllMultipleprintsCoreInfo } from "@/api/multipleprint/multipleprint";
import { setSelectionItem } from "@/api/section";
import { Button } from "@/components/ui/button";
import { SelectionCaption } from "@/components/cms/selection/SelectionCaption";
import { SelectionItemMedia } from "@/components/cms/selection/SelectionItemMedia";
import { DatatablePagination } from "@/components/ui/datatable-pagination";
import SearchBar from "@/components/ui/searchbar";
import { toast } from "@/hooks/use-toast";
import { SupportedLocale } from "@/i18n";
import { DateCF, MainTitleCF } from "@/lib/compute-field/shared/title";
import {
  CaptionAlignment,
  SectionDescriptionVisibility,
  Selection,
} from "@/lib/cms/section-types";
import { ArtworkCoreInfo, MultipleprintCoreInfo } from "@/lib/types";
import { cn } from "@/lib/utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check } from "lucide-react";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

const DEFAULT_PAGE_SIZE = 10;

type ItemTogglableProps = {
  item: ArtworkCoreInfo | MultipleprintCoreInfo;
  itemType: "artwork" | "multipleprint";
  onClick: (artworkId: string) => void;
  isToggled: boolean;
  caption?: Selection["caption"];
  visibility?: SectionDescriptionVisibility;
  captionAlignment?: CaptionAlignment;
};

const ItemTogglable = ({
  item,
  itemType,
  onClick,
  isToggled,
  caption,
  visibility,
  captionAlignment,
}: ItemTogglableProps) => {
  const { t, i18n } = useTranslation();
  const language = i18n.language as SupportedLocale;

  const handleClick = () => {
    onClick(item.id);
  };

  const isArtwork = itemType === "artwork";
  const registrationNumber = isArtwork
    ? (item as ArtworkCoreInfo).oeuvresDescriptionNumeroEnregistrement
    : (item as MultipleprintCoreInfo)
        .estampesMultiplesDescriptionNumeroEnregistrement;
  const title = isArtwork
    ? (item as ArtworkCoreInfo).oeuvresDescriptionTitre
    : (item as MultipleprintCoreInfo).estampesMultiplesDescriptionTitre;
  const titleState = isArtwork
    ? {
        unknown: (item as ArtworkCoreInfo).oeuvresDescriptionTitreInconnu,
        none: (item as ArtworkCoreInfo).oeuvresDescriptionTitreSansTitre,
        notConfirmed: (item as ArtworkCoreInfo).oeuvresValidationTitleNotConfirmed,
      }
    : {
        unknown: (item as MultipleprintCoreInfo)
          .estampesMultiplesDescriptionTitreInconnu,
        none: (item as MultipleprintCoreInfo)
          .estampesMultiplesDescriptionTitreSansTitre,
        notConfirmed: (item as MultipleprintCoreInfo)
          .estampesMultiplesDescriptionTitreIncertin,
      };
  const date = isArtwork
    ? (item as ArtworkCoreInfo).oeuvresDescriptionDateDeCreation
    : (item as MultipleprintCoreInfo)
        .estampesMultiplesDescriptionDateDeCreation;
  const dateUnknown = isArtwork
    ? (item as ArtworkCoreInfo).oeuvresDescriptionDateDeCreationInconnue
    : (item as MultipleprintCoreInfo)
        .estampesMultiplesDescriptionDateDeCreationInconnue;

  return (
    <div className="relative w-full min-w-0 flex flex-col gap-1">
      <div
        onClick={handleClick}
        className={cn(`hover:cursor-pointer ${isToggled ? "opacity-50" : ""}`)}
      >
        <SelectionItemMedia item={item as Selection["item"]} />
      </div>
      {isToggled && (
        <div className="absolute top-4 right-3 flex p-1 bg-blue-500 rounded">
          <Check size={14} color="white" />
        </div>
      )}
      {visibility && captionAlignment ? (
        <SelectionCaption
          item={item}
          caption={caption}
          visibility={visibility}
          captionAlignment={captionAlignment}
        />
      ) : (
        <div className="flex flex-col w-full py-2 gap-0.5 text-xs">
          {registrationNumber && (
            <p className="pb-1">
              <span className="text-gray-600">
                {t("registrationNumber")} :{" "}
              </span>
              <span className="font-semibold text-gray-950">
                {registrationNumber}
              </span>
            </p>
          )}
          <p className="flex flex-wrap items-baseline gap-x-1 gap-y-0">
            <b>
              <MainTitleCF
                input={{
                  title: title ?? "",
                  titleState,
                }}
                language={language}
              />
            </b>
            {(date || dateUnknown) && " — "}
            <DateCF
              input={{ date: date || undefined, unknownDate: dateUnknown }}
              language={language}
            />
          </p>
        </div>
      )}
    </div>
  );
};

type SelectableState = "unselected" | "selected" | "added" | "removed";

type SelectableEditState = Extract<SelectableState, "added" | "removed">;

type SelectableData<T extends SelectableState = SelectableEditState> = {
  id: string;
  state: T;
};

type SelectionFormData = {
  selectables: SelectableData[];
};

const updateSelectablesFormField = (
  selectables: SelectableData[],
  newSelectable: SelectableData<SelectableState>,
): SelectableData[] => {
  const newState = newSelectable.state;

  switch (newState) {
    case "unselected":
      return selectables.filter((s) => s.id !== newSelectable.id);

    case "added":
      return [
        ...selectables,
        newSelectable as SelectableData<SelectableEditState>,
      ];

    case "selected":
      return selectables.filter((s) => s.id !== newSelectable.id);

    case "removed":
      return [
        ...selectables,
        newSelectable as SelectableData<SelectableEditState>,
      ];
  }
};

const getStateWhenClicked = (
  currentState: SelectableState,
): SelectableState => {
  if (currentState === "added") {
    return "unselected";
  }
  if (currentState === "unselected") {
    return "added";
  }
  if (currentState === "removed") {
    return "selected";
  }
  if (currentState === "selected") {
    return "removed";
  }

  return "removed";
};

const useEditSelections = (
  sectionId: string,
  itemType: "artwork" | "multipleprint",
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (selectables: SelectableData[]) => {
      return await Promise.all(
        selectables
          .filter((s) => s.state === "added" || s.state === "removed")
          .map((s) =>
            setSelectionItem(sectionId, s.id, s.state === "added", itemType),
          ),
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["selection", sectionId] });
    },
  });
};

export const FlyingModale = ({ children }: { children: ReactNode }) => {
  return (
    <div className="fixed bottom-8 transform -translate-x-1/2 left-1/2 z-50">
      <div className="bg-white rounded-md shadow-md p-4">{children}</div>
    </div>
  );
};

const gridColsClass = {
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-4",
} as const;

export const AddSelectionToSection = ({
  sectionId,
  selectedImages,
  onIsDirtyChange,
  itemType,
  numberOfColumns = 4,
  visibility,
  captionAlignment,
}: {
  sectionId: string;
  selectedImages: Selection[];
  onIsDirtyChange?: (isDirty: boolean) => void;
  itemType: "artwork" | "multipleprint";
  numberOfColumns?: 2 | 3 | 4;
  visibility?: SectionDescriptionVisibility;
  captionAlignment?: CaptionAlignment;
}) => {
  const [pagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const [search, setSearch] = useState("");

  const { t } = useTranslation();

  const { data: allArtworks } = useGetAllArtworksCoreInfo(pagination, search);
  const { data: allMultipleprints } = useGetAllMultipleprintsCoreInfo(
    pagination,
    search,
  );

  const allItems = itemType === "artwork" ? allArtworks : allMultipleprints;

  const { mutateAsync: editSelections } = useEditSelections(
    sectionId,
    itemType,
  );

  const defaultValues = useMemo(() => {
    return {
      selectables: [],
    };
  }, []);

  const form = useForm<SelectionFormData>({
    defaultValues: defaultValues,
  });

  useEffect(() => {
    form.reset(defaultValues);
  }, [defaultValues, form]);

  const { control, handleSubmit } = form;

  const onSubmit = handleSubmit(async (data: SelectionFormData) => {
    editSelections(data.selectables, {
      onSuccess: () => {
        toast({
          title: t("cms.selection.sections.modale.success"),
          variant: "success",
          duration: 4000,
        });
        onIsDirtyChange?.(false);
        form.reset(defaultValues);
      },
      onError: () => {
        toast({
          title: t("cms.selection.sections.modale.error"),
          variant: "error",
          duration: 4000,
        });
      },
    });
  });

  const onChangeSearch = (search: string) => {
    setSearch(search);
    setPagination((prev) => ({
      ...prev,
      page: 0,
    }));
  };

  const displayedArtworks = allItems?.data ?? [];

  return (
    <div className="flex flex-col gap-8 pt-6">
      <SearchBar
        className="w-full max-w-[320px]"
        onChangeSearch={onChangeSearch}
        search={search}
      />
      <DatatablePagination
        pagination={pagination}
        setPage={setPagination}
        totalSize={allItems?.totalSize ?? 0}
      />

      <Controller
        name="selectables"
        control={control}
        render={({ field }) => {
          const addedSelectionsNb = field.value.filter(
            (s) => s.state === "added",
          ).length;

          const removedSelectionsNb = field.value.filter(
            (s) => s.state === "removed",
          ).length;

          const isDirty = addedSelectionsNb > 0 || removedSelectionsNb > 0;

          onIsDirtyChange?.(isDirty);

          return (
            <div
              className={cn("grid gap-6", gridColsClass[numberOfColumns])}
            >
              {displayedArtworks.map((artwork) => {
                const selectedSelection = selectedImages.find(
                  (s) => s.item.id === artwork.id,
                );
                const itemWithType =
                  selectedSelection?.item.imageType === itemType
                    ? selectedSelection.item
                    : ({
                        ...artwork,
                        imageType: itemType,
                      } as ArtworkCoreInfo | MultipleprintCoreInfo);
                const currentSelectable: SelectableData<SelectableState> =
                  field.value.find((s) => s.id === artwork.id) ??
                  (selectedSelection
                    ? {
                        state: "selected",
                        id: artwork.id,
                      }
                    : {
                        state: "unselected",
                        id: artwork.id,
                      });

                const isToggled =
                  currentSelectable?.state === "selected" ||
                  currentSelectable?.state === "added";

                return (
                  <ItemTogglable
                    key={artwork.id}
                    item={itemWithType}
                    itemType={itemType}
                    onClick={() => {
                      const current = currentSelectable;

                      const updated = {
                        ...current,
                        state: getStateWhenClicked(current.state),
                      };

                      const updatedSelectables = updateSelectablesFormField(
                        field.value,
                        updated,
                      );

                      field.onChange(updatedSelectables);
                    }}
                    isToggled={isToggled}
                    caption={selectedSelection?.caption}
                    visibility={visibility}
                    captionAlignment={captionAlignment}
                  />
                );
              })}

              {(addedSelectionsNb > 0 || removedSelectionsNb > 0) && (
                <FlyingModale>
                  <div className="flex flex-row gap-12 items-center">
                    <div className="w-full flex flex-col gap-4">
                      {addedSelectionsNb > 0 && (
                        <span>
                          {t("cms.selection.sections.modale.addedSelections", {
                            count: addedSelectionsNb,
                          })}
                        </span>
                      )}
                      {removedSelectionsNb > 0 && (
                        <span>
                          {t(
                            "cms.selection.sections.modale.removedSelections",
                            {
                              count: removedSelectionsNb,
                            },
                          )}
                        </span>
                      )}
                    </div>
                    <Button onClick={onSubmit}>
                      {t("cms.selection.sections.modale.submit")}
                    </Button>
                  </div>
                </FlyingModale>
              )}
            </div>
          );
        }}
      />
      <DatatablePagination
        pagination={pagination}
        setPage={setPagination}
        totalSize={allItems?.totalSize ?? 0}
      />
    </div>
  );
};

export default AddSelectionToSection;
